import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCustomAuth } from 'auth/AuthContext';
import useGetMyPermissions from 'hooks/queries/useGetMyPermissions';
import { AccessCentrePermissionKey } from 'models/enums';
import type { PermissionKey } from 'domains/accessCentre/types';

/**
 * Access Centre RBAC helpers. When LaunchDarkly `accessCenter` is off, all checks pass (legacy behavior).
 * When `accessCenter` is on, permission keys come from GET `/me` (`useGetMyPermissions`).
 */
export const useAccessCentrePermissions = () => {
    const { accessCenter: hasAccessCenterFlag } = useFlags();
    const { isAuthenticated } = useCustomAuth();

    const isGated = Boolean(hasAccessCenterFlag);

    const { data: meResponse, isLoading, isSuccess } = useGetMyPermissions({
        enabled: isAuthenticated && isGated,
    });

    const permissions: PermissionKey[] = isGated && isAuthenticated ? meResponse?.permissions ?? [] : [];

    /** True when Access Centre is on and GET /me succeeded with an empty `permissions` array. */
    const hasNoAccess = isGated && isAuthenticated && isSuccess && (meResponse?.permissions?.length ?? 0) === 0;

    const userHasPermissionKey = (key: PermissionKey) => permissions.includes(key);

    const allow = (key: PermissionKey) => !isGated || userHasPermissionKey(key);

    /** Either full PHI (`contacts.view`) or restricted (`contacts.view_non_phi`) — for viewing contacts page */
    const canAccessContacts =
        !isGated ||
        userHasPermissionKey(AccessCentrePermissionKey.ContactsView) ||
        userHasPermissionKey(AccessCentrePermissionKey.ContactsViewNonPhi);

    /** Show PHI-bearing columns (requires `contacts.view`; `contacts.view_non_phi` alone hides PHI) */
    const canViewContactsFullPhi = allow(AccessCentrePermissionKey.ContactsView);

    return {
        isAccessCentrePermissionsLoading: isLoading,
        hasNoAccess,
        hasAccessCentrePermission: (key: PermissionKey) => allow(key),

        canViewAccessManagement: allow(AccessCentrePermissionKey.AccessManagementView),
        canManageAccessManagement: allow(AccessCentrePermissionKey.AccessManagementManage),

        canViewContent: allow(AccessCentrePermissionKey.ContentView),
        canManageContent: allow(AccessCentrePermissionKey.ContentManage),
        canPublishContent: allow(AccessCentrePermissionKey.ContentPublish),

        canAccessContacts,
        canViewContactsFullPhi,
        canUploadContacts: allow(AccessCentrePermissionKey.ContactsUpload),

        canViewAudiences: allow(AccessCentrePermissionKey.AudiencesView),
        canManageAudiences: allow(AccessCentrePermissionKey.AudiencesManage),
        canViewAudienceContacts: allow(AccessCentrePermissionKey.AudiencesViewContacts),
        canDownloadAudienceContacts: allow(AccessCentrePermissionKey.AudiencesDownloadContacts),

        canViewValue: allow(AccessCentrePermissionKey.ValueView),
        canCreateValue: allow(AccessCentrePermissionKey.ValueCreate),

        canViewCurations: allow(AccessCentrePermissionKey.CurationsView),
        canManageCurationsDrafts: allow(AccessCentrePermissionKey.CurationsManageDrafts),
        canPublishCurations: allow(AccessCentrePermissionKey.CurationsPublish),
        canCurationsAbTesting: allow(AccessCentrePermissionKey.CurationsAbTesting),
        canViewCurationsAuditLog: allow(AccessCentrePermissionKey.CurationsAuditLog),

        canViewInsightsDashboards: allow(AccessCentrePermissionKey.InsightsViewDashboards),
        canDownloadInsightsCsvs: allow(AccessCentrePermissionKey.InsightsDownloadCsvs),
    };
};
